import { EChartService } from '@/service/echart/echart.service';
import { CompetencyBoxplotEchartService } from '@/service/echart/competency-boxplot.echart.service';
import { AvarageResult, StatsAllCompetencyGraph } from '@/models/statistics/stats-all-competency-graph';
import { AvgAndSdData } from '@/models/statistics/stats-boxplot-subtable-data';
import { StatsCharacterMapping } from '@/models/statistics/stats-character-mapping';
import { StatsCompetencyBoxplot } from '@/models/statistics/stats-competency-boxplot';
import { Competency } from '@/models/competency/competency';

export class StatisticsCompetencyGrowthEchartService extends EChartService {
  private readonly TARGET_COLOR = '#5CADFF';
  private readonly LINE_COLOR = '#ED4014';
  private zoomStartValue: Date;
  private zoomEndValue: Date;
  competencies: Competency[];
  competencyNameMap: Map<number, string>;
  competencyGrowthByCompetencyIDMap: Map<string, StatsAllCompetencyGraph>;
  subTableColmuns: any[];
  subTableData: any[];
  subTableAvgAndSd: AvgAndSdData;
  competencyBoxplotEchartService: CompetencyBoxplotEchartService = new CompetencyBoxplotEchartService();

  constructor(competencies: Competency[] = []) {
    super();
    this.series = [];
    this.competencyGrowthByCompetencyIDMap = new Map();
    this.competencies = competencies;
    this.competencyNameMap = new Map(this.competencies.map(c => [c.competencyID, c.name]));
    this.subTableColmuns = [];
    this.subTableData = [];
    this.subTableAvgAndSd = {};
  }

  createStatsAllCompetencyGraphs(serialNumber: string) {
    Object.keys(this.subTableAvgAndSd[serialNumber]).map(competencyID => {
      if (!this.competencyGrowthByCompetencyIDMap.has(competencyID)) {
        this.competencyGrowthByCompetencyIDMap.set(
          competencyID,
          new StatsAllCompetencyGraph().deserialize({ competencyID: Number(competencyID), competencyName: this.competencyNameMap.get(Number(competencyID)) })
        );
      }
      const statsAllCompetencyGraph = this.competencyGrowthByCompetencyIDMap.get(competencyID);
      const avarageResult = new AvarageResult().deserialize({ competencyScore: this.subTableAvgAndSd[serialNumber][competencyID].avg, serialNumber: serialNumber });
      statsAllCompetencyGraph.avarageResults.push(avarageResult);
    });
  }

  // コンピテンシーごとの過去のスコア数に応じてX軸のラベルのフォントサイズを変更する
  getXLableFontSize(len: number): number {
    switch (len) {
      case 1:
        return 12;
      case 2:
        return 12;
      case 3:
        return 11;
      case 4:
        return 9;
      case 5:
        return 10;
    }
  }

  // コンピテンシーごとの過去のスコアの数が4件以上の場合はX軸のラベル名を斜め表示にする
  getXLableRotate(len: number): number {
    switch (len) {
      case 1:
      case 2:
      case 3:
        return 0;
      case 4:
      case 5:
        return 15;
    }
  }

  // コンピテンシーごとに、コンピテンシーID を key 、options を value にして返却する。
  get options(): any {
    const self: any = this;
    let optionsMap: Map<number, any> = new Map();

    this.competencyGrowthByCompetencyIDMap.forEach((stats: StatsAllCompetencyGraph) =>
      optionsMap.set(stats.competencyID, {
        title: { left: 'center', top: -5 },
        grid: { width: '70%', height: '60%', top: 30, right: 50 },
        xAxis: {
          type: 'category',
          name: '系列',
          position: 'bottom',
          data: stats.avarageResults.map((result: AvarageResult) => result.serialNumber),
          axisLabel: {
            formatter: '{value}',
            rotate: this.getXLableFontSize(stats.avarageResults.length),
            fontSize: this.getXLableFontSize(stats.avarageResults.length),
          },
        },
        dataZoom: [
          {
            type: 'slider',
            // zoomLock: true,
            show: true,
            realtime: true,
            start: 0,
            end: 100,
            startValue: this.zoomStartValue,
            endValue: this.zoomEndValue,
            // filterMode: 'none',
            throttle: 0,
            // handleIcon: 'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
            // handleSize: '80%',
            // handleStyle: {
            //   color: '#fff',
            //   shadowBlur: 3,
            //   shadowColor: 'rgba(0, 0, 0, 0.6)',
            //   shadowOffsetX: 2,
            //   shadowOffsetY: 2
            // },
            xAxisIndex: [0],
          },
          // {
          //   type: 'inside',
          //   show: true,
          //   height: 15,
          //   start: 1,
          //   end: 35,
          // },
        ],
        tooltip: {
          formatter: function(params: any) {
            const dataIndex: number = params.dataIndex;
            const avarageResult: AvarageResult = self.statsAllCompetencyGraph.avarageResults[dataIndex];
            const title: string = `系列：${avarageResult.serialNumber}`;
            const percentScore: string = `スコア：${avarageResult.percentScore}`;
            return [title, percentScore].join('<br/>');
          },
        },
        yAxis: [
          {
            type: 'value',
            name: '変動率( % )',
            min: 0,
            max: 300,
          },
        ],
        series: this.series.filter(s => s.competencyID === stats.competencyID),
      })
    );

    return optionsMap;
  }

  addSeries(statsCompetencyDistributions: StatsCompetencyBoxplot[]) {
    const serialNumber = this.labels.slice(-1)[0];

    this.subTableAvgAndSd[serialNumber] = this.competencyBoxplotEchartService.prepareBoxplotSubTableData(statsCompetencyDistributions);
    if (Object.keys(this.subTableAvgAndSd[serialNumber]).length === 0) return;

    // this.competencyGrowthByCompetencyIDMap をセットする。
    this.createStatsAllCompetencyGraphs(serialNumber);

    const series: any[] = [];
    this.competencyGrowthByCompetencyIDMap.forEach((stats: StatsAllCompetencyGraph) => {
      series.push({
        type: 'line',
        symbolSize: 10,
        label: {
          normal: {
            show: true,
            position: 'top',
          },
        },
        competencyID: stats.competencyID,
        name: stats.competencyName,
        itemStyle: { color: this.TARGET_COLOR },
        data: stats.avarageResults.map((r: AvarageResult) => r.percentScore),
      });
    });

    this.series = [...series];
  }
}
